import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { getSyncStatus, SyncStatus } from "@/api/syncApi";

const TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, { status: SyncStatus | null; fetchedAt: number }>();
let generation = 0;
const listeners = new Set<() => void>();

/** Invalidate all ambient statuses — mounted hooks refetch. Called when the
 * sync panel closes (a pull/resolve inside it changes statuses). */
export function clearAmbientSyncCache(): void {
    cache.clear();
    generation += 1;
    listeners.forEach(fn => fn());
}

/**
 * Lightweight "did a PM change this in Jira/ADO?" probe for the editor.
 * Fires at most once per story per TTL; the backend's `ambient` mode reuses
 * its own ≤5-min-old remote check, so opening stories in quick succession
 * costs one tracker round-trip at most. Failures resolve to null — the
 * editor must render identically when sync is unavailable.
 */
export function useAmbientSyncStatus(storyId: string | undefined): SyncStatus | null {
    const { getToken } = useAuth();
    const getTokenRef = useRef(getToken);
    getTokenRef.current = getToken;
    const [status, setStatus] = useState<SyncStatus | null>(() => {
        if (!storyId) return null;
        const hit = cache.get(storyId);
        return hit && Date.now() - hit.fetchedAt < TTL_MS ? hit.status : null;
    });
    const [gen, setGen] = useState(generation);

    useEffect(() => {
        const bump = () => setGen(generation);
        listeners.add(bump);
        return () => { listeners.delete(bump); };
    }, []);

    useEffect(() => {
        if (!storyId) { setStatus(null); return; }
        const hit = cache.get(storyId);
        if (hit && Date.now() - hit.fetchedAt < TTL_MS) {
            setStatus(hit.status);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const token = await getTokenRef.current();
                const scope = await getSyncStatus({ type: "story", id: storyId }, token ?? "", { ambient: true });
                const row = scope.stories.find(s => s.story_id === storyId);
                const next = row?.status ?? null;
                cache.set(storyId, { status: next, fetchedAt: Date.now() });
                if (!cancelled) setStatus(next);
            } catch {
                if (!cancelled) setStatus(null);
            }
        })();
        return () => { cancelled = true; };
    }, [storyId, gen]);

    return status;
}
